import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';
import { contentBorders } from '../theme/createContentTheme';
import { headingCase } from '../utils/titleCase';

export interface SectionHeaderProps {
  title: React.ReactNode;
  /** Icon classifying the section (from `ESG_ICONS`). */
  icon?: SvgIconComponent;
  /** Supporting description under the title (max ~72ch). */
  description?: React.ReactNode;
  /** @deprecated use `description` */
  subtitle?: React.ReactNode;
  /** Metadata shown at the end of the title row (period chip, basis…). */
  meta?: React.ReactNode;
  /** Controls at the end of the title row. */
  actions?: React.ReactNode;
  /** `major` — h6 title, no rule; `minor` — hairline below, subtitle title. */
  level?: 'major' | 'minor';
  component?: React.ElementType;
  id?: string;
  sx?: SxProps<Theme>;
}

/**
 * Editorial section header (sections are separated by whitespace, no rule):
 *
 *   [icon] Environmental Performance              [March 2026]  ⋯
 *   Material collected through the platform this period.
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  icon: Icon,
  description,
  subtitle,
  meta,
  actions,
  level = 'major',
  component = 'h2',
  id,
  sx,
}) => {
  const { i18n } = useTranslation();
  const body = description ?? subtitle;
  const major = level === 'major';
  const heading = typeof title === 'string' ? headingCase(title, i18n.language) : title;

  return (
    <Box
      id={id}
      sx={[
        (theme) => ({
          mb: major ? 2.5 : 2,
          ...(major ? {} : { pb: 1.25, borderBottom: `1px solid ${contentBorders(theme).default}` }),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          columnGap: 2,
          rowGap: 1,
          minHeight: 32,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {Icon ? (
            <Icon aria-hidden sx={{ fontSize: major ? 20 : 18, color: 'primary.main', flexShrink: 0 }} />
          ) : null}
          <Typography
            variant={major ? 'h6' : 'subtitle1'}
            component={component}
            sx={{
              m: 0,
              color: 'text.primary',
              fontWeight: 600,
              lineHeight: 1.35,
            }}
          >
            {heading}
          </Typography>
        </Box>
        {meta || actions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {meta}
            {actions}
          </Box>
        ) : null}
      </Box>
      {body ? (
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: '72ch' }}>
          {body}
        </Typography>
      ) : null}
    </Box>
  );
};

export default SectionHeader;
export type { SectionHeaderProps as DashboardSectionHeaderProps };
